import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './LeadList.scss';
import { BASE_URL } from '../../../api/constants';
import requestInstance from '../../../services/AuthService/AccessTokenUpdateService';
import Pagination from '../../../components/Pagination/Pagination';

export const LeadList = () => {
  const location = useLocation();
  const searchParams = location.state || {};
  const { searchQuery, filters, fuzzyEnabled, showAll } = searchParams;
  
  const [leadList, setLeadList] = useState([]);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const itemsPerPage = 10; 

  const fetchLeads = async (url, params = {}) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Build query parameters
      let queryParams = new URLSearchParams();
      
      // Add pagination parameters
      queryParams.append('limit', params.limit || itemsPerPage);
      queryParams.append('offset', params.offset || 0);
      
      // Add search query if provided and not in "show all" mode
      if (params.query && !params.showAll) {
        queryParams.append('search', params.query);
      }
      
      // Add location filter if provided and not in "show all" mode
      if (params.location && !params.showAll) {
        queryParams.append('location', params.location);
      }
      
      // Support for fuzzy search if enabled and not in "show all" mode
      if (params.fuzzy && !params.showAll) {
        queryParams.append('fuzzy', 'true');
      }
      
      // Make API request with query parameters
      const fullUrl = `${url}${url.includes('?') ? '&' : '?'}${queryParams.toString()}`;
      console.log('Fetching leads with URL:', fullUrl);
      
      const response = await requestInstance.get(fullUrl);
      
      // Log the response for debugging
      console.log('Leads API response:', response.data);
      
      setLeadList(response.data.results || []);
      setNextPage(response.data.next);
      setPrevPage(response.data.previous);
      setCount(response.data.count || 0);
      setTotalPages(Math.ceil((response.data.count || 0) / itemsPerPage));
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      console.error('Error details:', error.response ? error.response.data : 'No response data');
      setError('Failed to load leads. Please try again.');
      // Set empty results to handle the error gracefully
      setLeadList([]);
      setCount(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Use the correct API endpoint for buyers, matching SearchService.js
    const baseUrl = `${BASE_URL}company/buyers/`;
    
    // If coming from search or showing all leads
    if (searchQuery || showAll || (filters && Object.keys(filters).length > 0)) {
      console.log('Search parameters or show all detected:', { searchQuery, filters, fuzzyEnabled, showAll });
      fetchLeads(baseUrl, {
        query: searchQuery,
        location: filters?.location,
        fuzzy: fuzzyEnabled,
        showAll: showAll,
        limit: itemsPerPage,
        offset: 0
      });
    } else {
      // Otherwise, fetch all leads
      fetchLeads(baseUrl, { limit: itemsPerPage, offset: 0 });
    }
  }, [searchQuery, filters, fuzzyEnabled, showAll]);

  // Handle pagination changes
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    const offset = (newPage - 1) * itemsPerPage;
    
    // Keep search parameters when changing pages, using the correct endpoint
    fetchLeads(`${BASE_URL}company/buyers/`, {
      query: searchQuery,
      location: filters?.location,
      fuzzy: fuzzyEnabled,
      showAll: showAll,
      limit: itemsPerPage,
      offset
    });
  };

  return (
    <div className="lead-list">
      {/* Display search header if search is active */}
      {searchQuery && !showAll && (
        <div className="lead-list__search-header">
          <h1>Search Results: {searchQuery}</h1>
          <div className="lead-list__search-info">
            <p>Found {count} potential buyers matching your search</p>
            {filters?.location && (
              <div className="lead-list__filter-tag">
                <span>Location: {filters.location}</span>
              </div>
            )}
            {fuzzyEnabled && (
              <div className="lead-list__search-mode">
                <span>Smart Search enabled</span>
              </div>
            )}
          </div>
        </div>
      )}
      
      {showAll && (
        <div className="lead-list__search-header">
          <h1>All Leads</h1>
          <div className="lead-list__search-info">
            <p>Showing all available buyers</p>
          </div>
        </div>
      )}
      
      {!searchQuery && !showAll && <h1>My Leads</h1>}
      
      {isLoading ? (
        <div className="lead-list__loading">Loading leads...</div>
      ) : error ? (
        <div className="lead-list__error">{error}</div>
      ) : leadList.length === 0 ? (
        <div className="lead-list__empty">
          <p>No leads found{searchQuery && !showAll ? ` matching your search` : ''}.</p>
        </div>
      ) : (
        <div className="lead-list__container">
          <table className="lead-list__table">
            <thead>
              <tr>
                <th>Client Name</th>
                <th>Location</th>
                <th>Category</th>
                <th>Contact</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leadList.map(lead => (
                <tr key={lead.id}>
                  <td>{lead.brand_name || 'N/A'}</td>
                  <td>{lead.country || 'N/A'}</td>
                  <td>{lead.category || 'N/A'}</td>
                  <td>{lead.email || 'N/A'}</td>
                  <td><button className="contact-button">Contact</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <Pagination
              itemsPerPage={itemsPerPage}
              setObjects={setLeadList}
              setNextPage={setNextPage}
              setPrevPage={setPrevPage}
              nextPage={nextPage}
              prevPage={prevPage}
              currentPage={currentPage}
              totalPages={totalPages}
              setCurrentPage={handlePageChange}
              count={count}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default LeadList;
